import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Button from '../ui/Button';
import Input from '../ui/Input';

const schema = z.object({
  name: z.string().min(1, 'Block name is required').max(255),
  description: z.string().optional(),
  area_hectares: z.number().positive().optional(),
  sort_order: z.number().int('A whole number').min(0, 'Zero or more').nullable().optional(),
  active: z.boolean().optional(),
});

export type BlockFieldData = z.infer<typeof schema>;

interface BlockFormProps {
  initialValues?: Partial<BlockFieldData>;
  areaFromMap?: number | null;
  onSubmit: (data: BlockFieldData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  hasPolygon?: boolean;
}

function BlockForm({
  initialValues,
  areaFromMap,
  onSubmit,
  onCancel,
  isSubmitting = false,
  hasPolygon = false,
}: BlockFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BlockFieldData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initialValues?.name || '',
      description: initialValues?.description || '',
      area_hectares: initialValues?.area_hectares,
      sort_order: initialValues?.sort_order ?? null,
      active: initialValues?.active !== false,
    },
  });

  useEffect(() => {
    if (areaFromMap != null) {
      setValue('area_hectares', areaFromMap);
    }
  }, [areaFromMap, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Block Name *"
        placeholder="e.g., Block A, North Block"
        error={errors.name?.message}
        {...register('name')}
      />

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">Description</label>
        <textarea
          className="input-professional w-full resize-none"
          rows={3}
          placeholder="Optional notes about this block..."
          {...register('description')}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-neutral-700">
          Area (hectares)
        </label>
        <input
          type="number"
          step="0.01"
          className="input-professional w-full bg-neutral-50"
          placeholder="Auto-calculated from polygon"
          {...register('area_hectares', { valueAsNumber: true })}
        />
        {areaFromMap && (
          <p className="text-xs text-success-600">
            ✓ Area calculated from drawn polygon
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="block-sort-order" className="block text-sm font-semibold text-neutral-700">
          Sequence
        </label>
        <input
          id="block-sort-order"
          type="number"
          min="0"
          step="1"
          className="input-professional w-full"
          placeholder="e.g. 10"
          {...register('sort_order', { setValueAs: v => (v === '' || v == null ? null : Number(v)) })}
        />
        {errors.sort_order?.message && <p className="text-xs text-red-600">{errors.sort_order.message}</p>}
        <p className="text-xs text-neutral-500">
          Lower numbers come first wherever this farm&apos;s blocks are listed, and in the picking walk.
          Blocks with no sequence follow, by name.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="active"
          className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          {...register('active')}
          defaultChecked
        />
        <label htmlFor="active" className="text-sm text-neutral-700">Active block</label>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          disabled={!hasPolygon}
        >
          Save Block
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {!hasPolygon && (
        <p className="text-xs text-amber-600">
          Draw a block boundary on the map before saving
        </p>
      )}
    </form>
  );
}

export default BlockForm;
